import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Divider from "@/components/Divider";
import Icon from "@/components/Icon";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import CustomPriceInput from "./CustomPriceInput";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import type { EventFeesFormValues } from "../types";

interface VariantOption {
  id: string;
  value: string;
  displayText: string;
  name: string;
  priceCents: string;
}

interface SeminarFeeFieldsProps {
  hasRegistrations: boolean;
  hasMultipleFees: boolean;
  hasTemplateVariants: boolean;
  index: number;
  onRemove: () => void;
  showRemoveButton: boolean;
  variantOptions: VariantOption[];
  variantsLoading: boolean;
}

const FEE_COLLECTOR_OPTIONS = [
  { value: "catalog", displayText: "PGA" },
  { value: "external", displayText: "Third-Party" }
];

const PGA_COLLECTION_METHOD_OPTIONS = [{ value: "registration", displayText: "During Registration" }];
const THIRD_PARTY_COLLECTION_METHOD_OPTIONS = [{ value: "on_site", displayText: "Onsite" }];

export default function SeminarFeeFields({
  hasRegistrations,
  hasMultipleFees,
  hasTemplateVariants,
  index,
  onRemove,
  showRemoveButton,
  variantOptions,
  variantsLoading
}: SeminarFeeFieldsProps) {
  const {
    control,
    watch,
    setValue,
    clearErrors,
    trigger,
    formState: { errors }
  } = useFormContext<EventFeesFormValues>();
  const kind = watch(`eventFees.${index}.kind`);
  const currentVariantId = watch(`eventFees.${index}.productVariantId`);
  const currentVariantName = watch(`eventFees.${index}.productVariantName`);
  const currentAmountCents = watch(`eventFees.${index}.amountCents`);
  const displayedCollectionMethod = kind === "external" ? "on_site" : "registration";
  const collectionMethodOptions =
    kind === "external" ? THIRD_PARTY_COLLECTION_METHOD_OPTIONS : PGA_COLLECTION_METHOD_OPTIONS;

  const mergedVariantOptions = useMemo(() => {
    if (!currentVariantId || variantOptions.some((option) => option.value === currentVariantId)) {
      return variantOptions;
    }

    return [
      {
        id: currentVariantId,
        value: currentVariantId,
        displayText: `${currentVariantName} (${centsToDollarsFormatted(currentAmountCents)})`,
        name: currentVariantName,
        priceCents: String(currentAmountCents)
      },
      ...variantOptions
    ];
  }, [currentAmountCents, currentVariantId, currentVariantName, variantOptions]);

  const variantSupportingText = !hasTemplateVariants
    ? "Pricing options will load from the seminar defaults."
    : variantsLoading
      ? "Loading pricing options..."
      : mergedVariantOptions.length === 0
        ? "No pricing options are available for this template."
        : undefined;

  const removeButton =
    showRemoveButton && hasMultipleFees && !hasRegistrations ? (
      <button
        type="button"
        className="h-14 flex items-center justify-center px-2 text-sys-brand-on-surface"
        onClick={onRemove}
        aria-label="Remove fee"
      >
        <Icon name="delete" />
      </button>
    ) : null;

  return (
    <>
      {index > 0 && (
        <div className="py-3">
          <Divider />
        </div>
      )}
      <div className="flex flex-col gap-6">
        <div className="flex items-start gap-2">
          <div className="grid w-full gap-6 md:grid-cols-2">
            <Controller
              name={`eventFees.${index}.kind`}
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  label="Fee Collector"
                  options={FEE_COLLECTOR_OPTIONS}
                  className="w-full"
                  value={value}
                  disabled={hasRegistrations}
                  onSelect={(nextValue) => {
                    onChange(nextValue);
                    setValue(
                      `eventFees.${index}.paymentCollection`,
                      nextValue === "external" ? "external_system" : "registration"
                    );

                    if (nextValue === "external") {
                      setValue(`eventFees.${index}.productVariantId`, "");
                      setValue(`eventFees.${index}.productVariantName`, "");
                      setValue(`eventFees.${index}.amountCents`, 0);
                    } else {
                      setValue(`eventFees.${index}.displayName`, "");
                    }

                    trigger(`eventFees.${index}`);
                  }}
                />
              )}
            />
            <Select
              label="Collection Method"
              options={collectionMethodOptions}
              className="w-full"
              value={displayedCollectionMethod}
              disabled
            />
          </div>
          {removeButton}
        </div>

        {kind === "external" ? (
          <>
            <div className="grid gap-6 md:grid-cols-2">
              <Controller
                name={`eventFees.${index}.displayName`}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    label="Override Label"
                    className="w-full"
                    value={value}
                    disabled={hasRegistrations}
                    onChange={(event) => {
                      onChange(event);
                      const nextValue = (event.target as HTMLInputElement).value;

                      if (nextValue.trim()) {
                        clearErrors(`eventFees.${index}.displayName`);
                      } else {
                        trigger(`eventFees.${index}.displayName`);
                      }
                    }}
                    required
                    error={!!errors.eventFees?.[index]?.displayName}
                    errorText={errors.eventFees?.[index]?.displayName?.message as string}
                  />
                )}
              />
              <CustomPriceInput
                disabled={hasRegistrations}
                name={`eventFees.${index}.amountCents`}
                label="Price"
                defaultValue={currentAmountCents}
                onValueChange={(values) => {
                  setValue(`eventFees.${index}.amountCents`, Math.round(values.floatValue || 0));
                }}
                error={!!errors.eventFees?.[index]?.amountCents}
                errorText={errors.eventFees?.[index]?.amountCents?.message as string}
              />
            </div>
          </>
        ) : (
          <div className="grid gap-6">
            <Controller
              name={`eventFees.${index}.productVariantId`}
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  label="Product Variant"
                  options={mergedVariantOptions}
                  className="w-full"
                  value={value}
                  disabled={hasRegistrations || !hasTemplateVariants || mergedVariantOptions.length === 0}
                  supportingText={variantSupportingText}
                  onSelect={(nextValue) => {
                    onChange(nextValue);
                    const selectedVariant = mergedVariantOptions.find((option) => option.value === nextValue);

                    setValue(`eventFees.${index}.productVariantId`, nextValue);
                    setValue(`eventFees.${index}.productVariantName`, selectedVariant?.name ?? "");
                    setValue(`eventFees.${index}.amountCents`, parseInt(selectedVariant?.priceCents ?? "0", 10));

                    if (nextValue) {
                      clearErrors(`eventFees.${index}.productVariantId`);
                    } else {
                      trigger(`eventFees.${index}.productVariantId`);
                    }
                  }}
                  required
                  data-testid={`event-fee-variant-select-${index}`}
                  error={!!errors.eventFees?.[index]?.productVariantId}
                  errorText={errors.eventFees?.[index]?.productVariantId?.message as string}
                />
              )}
            />
          </div>
        )}
      </div>
    </>
  );
}
